import React, { useCallback } from "react";
import { APPOINTMENT_SELF_SERVICE_URL, buildUrl, CSS_MY_JOBS_URL, CSS_PAGE_URL } from "../../config/urls";
import { useParams } from "react-router-dom";
import { isDragonstone, isSalesforceApplication } from "../../helpers/url-params";
import { ManageApplicationCardUncontrolled } from "./card-uncontrolled";
import { Stage } from "../../helpers/get-settings";
import * as adobeAnalytics from "../../utility/adobe-analytics";
import {
  getJobScheduleDetails,
  getApplicationManageData,
  getStartDateInfo,
  getNHEAppointment,
  getSalesforceJobDetails,
} from "../../utility/application-data";
import { isCustomerService } from "../../utility/app-config-helper";

interface ManageApplicationCardProps {
  stage?: Stage;
  collapsed: boolean;
}

export const ManageApplicationCard = ({ stage, collapsed }: ManageApplicationCardProps) => {
  const { requisitionId, applicationId } = useParams<{
    requisitionId: string;
    applicationId: string;
  }>();

  const jcAccessor = useCallback(() => {
    return getJobScheduleDetails(applicationId);
  }, []) as any;

  const salesforceAccessor = useCallback(() => {
    return getSalesforceJobDetails(applicationId);
  }, []) as any;

  const sdaAccessor = useCallback(() => {
    return getStartDateInfo(applicationId);
  }, []) as any;

  const nheAccessor = useCallback(() => {
    return getNHEAppointment(applicationId);
  }, []) as any;

  const applicationManageAccessor = useCallback(() => {
    return getApplicationManageData(applicationId);
  }, []) as any;

  if (!isDragonstone(requisitionId) && !isCustomerService(applicationId)) {
    return null;
  }

  const sendAnalyticsEvent = useCallback(
    async (eventName: string) => {
      if (!isSalesforceApplication(applicationId)) {
        const scheduleDetails = await getJobScheduleDetails(applicationId);
        await adobeAnalytics.addEventMetric(window, applicationId, eventName, {
          dragonstoneSchedule: {
            scheduleID: scheduleDetails?.scheduleId,
            jobID: requisitionId,
          },
          geocluster: {
            ID: scheduleDetails?.geoClusterId,
          },
        });
      }
    },
    [applicationId]
  );

  const sendWithdrawApplicationAnalyticsEvent = useCallback(
    async (eventName: string, withdrawReason: string) => {
      const scheduleDetails = await getJobScheduleDetails(applicationId);
      await adobeAnalytics.addEventMetric(window, applicationId, eventName, {
        dragonstoneSchedule: {
          scheduleID: scheduleDetails?.scheduleId,
        },
        dragonstoneJob: {
          jobID: requisitionId,
        },
        geocluster: {
          ID: scheduleDetails?.geoClusterId,
        },
        withdraw: {
          reason: withdrawReason,
        },
      });
    },
    [applicationId]
  );

  const trackScheduleChangeClick = useCallback(() => sendAnalyticsEvent("first day change link click"), [
    sendAnalyticsEvent,
  ]);

  const trackWithdrawApplicationClick = useCallback(() => sendWithdrawApplicationAnalyticsEvent("click withdraw", ""), [
    sendAnalyticsEvent,
  ]);

  const trackWithdrawApplicationConfirmButtonClick = useCallback(
    (withdrawReason: string) => sendWithdrawApplicationAnalyticsEvent("select withdraw reason", withdrawReason),
    [sendAnalyticsEvent]
  );

  const cssURL = buildUrl(CSS_PAGE_URL, {
    stage,
    requisitionId,
    applicationId,
  });

  const appointmentSelfServiceURL = buildUrl(APPOINTMENT_SELF_SERVICE_URL, {
    stage,
    applicationId,
    requisitionId,
  });

  const dashboardURL = buildUrl(CSS_MY_JOBS_URL, {
    stage,
  });

  return (
    <ManageApplicationCardUncontrolled
      applicationId={applicationId}
      salesforceAccessor={salesforceAccessor}
      jcAccessor={jcAccessor}
      sdaAccessor={sdaAccessor}
      nheAccessor={nheAccessor}
      applicationManageAccessor={applicationManageAccessor}
      cssURL={cssURL}
      dashboardURL={dashboardURL}
      appointmentSelfServiceURL={appointmentSelfServiceURL}
      trackScheduleChangeClick={trackScheduleChangeClick}
      trackWithdrawApplicationClick={trackWithdrawApplicationClick}
      trackWithdrawApplicationConfirmButtonClick={trackWithdrawApplicationConfirmButtonClick}
      collapsed={collapsed}
    />
  );
};
